/**
 * Size Guides Data Policy (TV1-116 #13)
 *
 * Query-first + subscribe data policy for the channel-neutral size-guide gallery.
 * Mirrors the structure of priceList's data policy but is much simpler: size
 * guides have no server-side search/filter/pagination state (the gallery filters
 * client-side and guides are O(10s)), so this policy owns a single readmodel
 * data source.
 *
 * Lifecycle (`init`):
 * 1. QUERY FIRST — `backend.queries.ItemsPublishingSizeGuidesSearch({...})` fetches
 *    the first page and dispatches `sizeGuides/data/setPageResult` ({ source: 'api' }).
 *    `normalize` (invariants) derives `data.galleryItems` from `data.guides`, so
 *    the policy never dispatches gallery items directly.
 * 2. SUBSCRIBE — opens the readmodel subscription in pv3 **signal mode**
 *    (`{ _snapshot: false, _changes: 'signal' }`). The server emits
 *    `{ type: 'signal' }` (no payload) on any change. The client lib must NOT be
 *    asked to stream snapshots/deltas here — the refetch is owned by this policy.
 * 3. SIGNAL → DEBOUNCED REFETCH — each `signal` (re)schedules a ~200ms debounce;
 *    when it fires the policy re-runs the `ItemsPublishingSizeGuides` query and
 *    re-dispatches `setGuides`. Debouncing coalesces bursts of changes into a
 *    single refetch (see priceList's signal-subscription note).
 * 4. CLEANUP — clears the pending debounce timer and closes the subscription on
 *    unmount.
 *
 * NOTE (client library): `backend.queries.ItemsPublishingSizeGuides` and
 * `backend.subscriptions.ItemsPublishingSizeGuides` are the generated client
 * methods for the `/items-publishing/size-guides` readmodel route (base +
 * subscription). The search variant maps to
 * `backend.queries.ItemsPublishingSizeGuidesSearch`
 * (`/items-publishing/size-guides-search`). These are not present in the
 * generated client types until the readmodel routes are wired and the client is
 * regenerated at platform validation — see the TODO guard below. The gallery
 * does not page/filter server-side, so only the base query+subscription is used
 * here; the `-search` variant is for the future filter/paginate UI.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { SizeGuideReadmodelType, SizeGuidesState } from '../state';
import { SizeGuidesCommand } from '../commands';

/**
 * Debounce window applied to subscription signals before refetching. Kept tight
 * (~200ms) per SDD §"realtime signal-mode refetch" — long enough to coalesce a
 * burst of changes, short enough to feel live.
 */
const SIGNAL_DEBOUNCE_MS = 200;

/**
 * Debounce window applied to a search-term change before refetching — coalesces
 * keystrokes into one server round-trip. Page clicks and CRUD-refetch are NOT
 * debounced (they should feel instant).
 */
const SEARCH_DEBOUNCE_MS = 300;

/** A pv3 readmodel subscription handle. */
type SubscriptionHandle = { close: () => void };

/** A single change frame in signal mode (no data payload — just the trigger). */
type SizeGuideSubscriptionSignal = { type?: 'signal' | string };

/** A single page of guides from the `-search` route. */
type SizeGuidesSearchResult = {
  items: SizeGuideReadmodelType[];
  count: number;
};

/**
 * Generated query method for the paginated `/items-publishing/size-guides-search`
 * route. `search` maps to the `:search?` path param; `limit`/`offset` paginate.
 * Returns the current page plus the full server-side `count`.
 */
type SizeGuidesQueries = {
  ItemsPublishingSizeGuidesSearch: (
    params: { search?: string; limit: number; offset: number }
  ) => Promise<SizeGuidesSearchResult>;
};

/** Generated subscription method for the `/items-publishing/size-guides` route. */
type SizeGuidesSubscriptions = {
  ItemsPublishingSizeGuides: (
    params: Record<string, unknown>,
    onChange: ( change: SizeGuideSubscriptionSignal ) => void
  ) => SubscriptionHandle;
};

type SizeGuidesDispatch = (
  command: SizeGuidesCommand,
  meta: { source: string }
) => void;

export const createSizeGuidesDataPolicy = (
  backend: ClientLibraryType
): Policy<SizeGuidesState, SizeGuidesCommand> => {
  // Backend contract (TV1-116 — /items-publishing/size-guides-search):
  //   queries.ItemsPublishingSizeGuidesSearch({ search?, limit, offset })
  //     => { items: SizeGuideReadmodelType[]; count: number }
  //   subscriptions.ItemsPublishingSizeGuides({ _snapshot: false,
  //     _changes: 'signal' }, onChange) => { close }
  // The base `/size-guides/:id?` route still backs the subscription; the gallery
  // reads pages from the `-search` route so it scales to thousands of guides
  // without loading them all.
  //
  // TODO(TV1-116): drop these casts once the client library is regenerated with
  // typed size-guide readmodel routes. Scoped to `queries`/`subscriptions` only,
  // mirroring `oneTapInitPolicy`'s `backend.commands as unknown as { ... }` idiom.
  const queries = backend.queries as unknown as SizeGuidesQueries;
  const subscriptions = backend.subscriptions as unknown as SizeGuidesSubscriptions;
  let subscription: SubscriptionHandle | null = null;
  let signalTimer: ReturnType<typeof setTimeout> | null = null;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  type ListState = SizeGuidesState['data']['list'];

  /** Fetch the page described by `list` and publish it to state (api source). */
  const fetchPage = async ( dispatch: SizeGuidesDispatch, list: ListState ) => {
    dispatch(
      { type: 'sizeGuides/data/setLoading', payload: { loading: true }},
      { source: 'api' }
    );
    try {
      const term = list.searchQuery.trim();
      const offset = Math.max( 0, ( list.page - 1 ) * list.pageSize );
      const result = await queries.ItemsPublishingSizeGuidesSearch({
        ...( term ? { search: term } : {}),
        limit: list.pageSize,
        offset
      });
      dispatch(
        {
          type: 'sizeGuides/data/setPageResult',
          payload: { guides: result?.items ?? [], count: result?.count ?? 0 }
        },
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( 'Failed to fetch size guides:', error );
      dispatch(
        { type: 'sizeGuides/data/setLoading', payload: { loading: false }},
        { source: 'api' }
      );
    }
  };

  return {
    name: 'size-guides-data',

    // React to: (a) a page change, (b) a search-term change, or (c) the CRUD
    // queue draining (create/edit/delete/import all funnel through pendingCrud).
    // (c) is the read-after-write guard — the realtime signal is primary but can
    // be missed (e.g. a websocket reconnect right after an import burst).
    shouldReact: ( prev, next ) =>
      prev.data.list.page !== next.data.list.page ||
      prev.data.list.searchQuery !== next.data.list.searchQuery ||
      ( prev.loading.pendingCrud.length > 0 && next.loading.pendingCrud.length === 0 ),

    onStateChange: async ({ prev, next, ctx }) => {
      const dispatch = ctx.dispatch as SizeGuidesDispatch;

      // A search-term change is debounced (coalesce keystrokes). It also resets
      // page to 1 in the same transition, so the debounced fetch reads the
      // freshest list slice from getState() when it fires.
      if ( prev.data.list.searchQuery !== next.data.list.searchQuery ) {
        if ( searchTimer ) clearTimeout( searchTimer );
        searchTimer = setTimeout(() => {
          searchTimer = null;
          fetchPage( dispatch, ctx.getState().data.list );
        }, SEARCH_DEBOUNCE_MS );
        return;
      }

      // Page change or CRUD-drain refetch — immediate.
      await fetchPage( dispatch, next.data.list );
    },

    init: ({ dispatch, getState }) => {
      const d = dispatch as SizeGuidesDispatch;

      // 1. Query first — populate the first page.
      fetchPage( d, getState().data.list );

      // 2. Subscribe in signal-only mode — the server sends `{ type: 'signal' }`
      //    on any change; this policy owns the refetch (refetches the CURRENT
      //    page so the active view stays live without losing the user's spot).
      subscription = subscriptions.ItemsPublishingSizeGuides(
        { _snapshot: false, _changes: 'signal' },
        ( change ) => {
          if ( change?.type !== 'signal' ) return;
          if ( signalTimer ) clearTimeout( signalTimer );
          signalTimer = setTimeout(() => {
            signalTimer = null;
            fetchPage( d, getState().data.list );
          }, SIGNAL_DEBOUNCE_MS );
        }
      );

      // 3. Cleanup on unmount.
      return () => {
        if ( signalTimer ) {
          clearTimeout( signalTimer );
          signalTimer = null;
        }
        if ( searchTimer ) {
          clearTimeout( searchTimer );
          searchTimer = null;
        }
        if ( subscription ) {
          subscription.close();
          subscription = null;
        }
      };
    }
  };
};
